import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SubmitChecklist } from "@/components/SubmitChecklist";

const DECLARATION = "Declaro que toda la información y documentación proporcionada en esta solicitud es verdadera, completa y fidedigna. Entiendo que EIM podrá solicitar antecedentes adicionales para verificar la información entregada.";

export default function StepSubmit({ app, patch, onSubmitted, flush, goStep }) {
  const [accepted, setAccepted] = useState(!!app.declaration?.accepted);
  const [requested, setRequested] = useState(app.requested_pct ?? 0);
  const [acceptCommittee, setAcceptCommittee] = useState(!!app.accept_committee_pct);
  const [mode, setMode] = useState(app.accept_committee_pct ? "committee" : (app.requested_pct > 0 ? "request" : null));
  const pctMissing = !acceptCommittee && !(requested > 0);
  const [needExplanation, setNeedExplanation] = useState(app.need_explanation || "");
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const choose = (m) => {
    setMode(m);
    const committee = m === "committee";
    setAcceptCommittee(committee);
    patch({ accept_committee_pct: committee });
  };
  const setPct = (v) => {
    const n = Math.max(0, Math.min(100, v));
    setRequested(n);
    patch({ requested_pct: n });
  };

  const submit = async () => {
    if (pctMissing) { toast.error("Falta definir el porcentaje a solicitar, o marca que aceptas lo que decida el comité."); return; }
    if (!accepted) { toast.error("Debes aceptar la declaración jurada."); return; }
    if (needExplanation.trim().length < 10) { toast.error("Cuéntanos brevemente por qué necesitas la beca."); return; }
    setSubmitting(true);
    try {
      await flush();
      await api.post(`/applications/${app.id}/submit`, {
        declaration_accepted: accepted,
        requested_pct: acceptCommittee ? null : (requested ?? 0),
        accept_committee_pct: acceptCommittee,
        need_explanation: needExplanation,
      });
      toast.success("¡Solicitud enviada!");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConfirm(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-1">Envío</h1>
        <p className="text-muted-foreground">Último paso: declaración jurada y porcentaje solicitado.</p>
      </div>

      <SubmitChecklist app={app} goStep={goStep} />

      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="text-lg font-semibold mb-3">Porcentaje de beca que solicitas</h2>
        <p className="text-sm text-muted-foreground mb-5">Elige una de las dos opciones:</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button type="button" onClick={() => choose("request")} data-testid="option-request-pct"
            className={`text-left rounded-xl border-2 p-4 transition-colors ${mode === "request" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
            <p className="font-semibold">Quiero pedir un porcentaje</p>
            <p className="text-sm text-muted-foreground">Indico cuánto descuento de la cuota solicito.</p>
          </button>
          <button type="button" onClick={() => choose("committee")} data-testid="accept-committee-checkbox"
            className={`text-left rounded-xl border-2 p-4 transition-colors ${mode === "committee" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
            <p className="font-semibold">Acepto lo que decida el comité</p>
            <p className="text-sm text-muted-foreground">EIM define el porcentaje según la evaluación socioeconómica.</p>
          </button>
        </div>
        {mode === "request" && (
          <div className="mt-6" data-testid="request-pct-panel">
            <p className="text-sm text-muted-foreground mb-3">Mueve la barra o usa los botones. Ejemplo: 50% significa que pagarías la mitad de tu cuota.</p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <Button type="button" variant="outline" className="h-12 w-12 rounded-full text-xl border-2" onClick={() => setPct(requested - 5)} data-testid="btn-pct-minus">−</Button>
              <div className="text-5xl font-black text-primary w-32 text-center" data-testid="requested-pct-value">{requested > 0 ? `${requested}%` : "—"}</div>
              <Button type="button" variant="outline" className="h-12 w-12 rounded-full text-xl border-2" onClick={() => setPct(requested + 5)} data-testid="btn-pct-plus">+</Button>
            </div>
            <Slider value={[requested]} min={0} max={100} step={1} onValueChange={(v) => setPct(v[0])} data-testid="requested-pct-slider" />
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {[25, 50, 75, 100].map((p) => (
                <Button key={p} type="button" variant={requested === p ? "default" : "outline"} className="rounded-full h-10 px-5" onClick={() => setPct(p)} data-testid={`btn-pct-${p}`}>{p}%</Button>
              ))}
            </div>
          </div>
        )}
        {pctMissing && (
          <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="pct-missing-warning">
            Falta definir el porcentaje a solicitar, o marcar que aceptas lo que decida el comité.
          </p>
        )}

        <div className="mt-6 border-t border-border pt-6">
          <label className="text-sm font-semibold uppercase tracking-wide text-slate-600">¿Por qué necesitas la beca?</label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Cuéntanos brevemente la situación de tu familia. Esto ayuda al comité a entender tu caso.</p>
          <Textarea
            className="rounded-xl min-h-[120px]"
            maxLength={1000}
            value={needExplanation}
            onChange={(e) => { setNeedExplanation(e.target.value); patch({ need_explanation: e.target.value }); }}
            placeholder="Escribe aquí…"
            data-testid="need-explanation"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">{needExplanation.length}/1000</p>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="text-lg font-semibold mb-3">Declaración jurada</h2>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="declaration-checkbox" className="mt-1" />
          <span className="text-sm text-foreground">{DECLARATION}</span>
        </label>
      </section>

      <section className="bg-secondary/60 border border-border rounded-2xl p-6 flex gap-3">
        <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
        <p className="text-sm">Al enviar, tu solicitud quedará bloqueada para edición y se le asignará un identificador único. Recibirás un email de confirmación.</p>
      </section>

      {!confirm ? (
        <Button onClick={() => { if (pctMissing) { toast.error("Falta definir el porcentaje a solicitar, o marca que aceptas lo que decida el comité."); return; } if (!accepted) { toast.error("Debes aceptar la declaración jurada."); return; } setConfirm(true); }}
          className="btn-lift w-full h-14 text-lg rounded-xl" data-testid="btn-submit-application">
          Enviar solicitud
        </Button>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-primary p-6 space-y-4">
          <p className="font-semibold">¿Confirmas el envío de tu solicitud?</p>
          <p className="text-sm text-muted-foreground">No podrás editarla después, salvo que EIM te solicite información adicional.</p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setConfirm(false)} className="rounded-xl border-2" disabled={submitting}>Cancelar</Button>
            <Button onClick={submit} disabled={submitting} className="btn-lift rounded-xl flex-1" data-testid="btn-confirm-submit">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sí, enviar solicitud"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
